import React, { useEffect, useMemo, useState } from "react";
import { api, formatZAR, formatDate, MONTHS } from "../api";
import { InvoiceStatusBadge, RepeatBadge } from "../components/StatusBadge";
import { Filter, Save, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import BusinessFilter from "../components/BusinessFilter";
import { useBusiness } from "../context/BusinessContext";

export default function Outstanding() {
  const { business } = useBusiness();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  // Date range — inclusive from `dateFrom` (first-of-month) to `dateTo`
  // (last-of-month) using ISO strings, so the same filter works whether the
  // user picks 1 month, 3 months or a full year.
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const [startYear, setStartYear] = useState(y);
  const [startMonth, setStartMonth] = useState(m);
  const [endYear, setEndYear] = useState(y);
  const [endMonth, setEndMonth] = useState(m);
  const [rangeActive, setRangeActive] = useState(false);
  const [editingRef, setEditingRef] = useState({});
  // Sort — column headers toggle asc/desc; default is Client A→Z, then Task A→Z.
  const [sortField, setSortField] = useState("client");
  const [sortDir, setSortDir] = useState("asc");

  const lastDayOfMonth = (yr, mo) => new Date(yr, mo, 0).getDate();

  const load = async () => {
    const params = { invoice_status: "outstanding" };
    if (clientFilter) params.client_id = clientFilter;
    else if (business) params.business = business;
    if (rangeActive) {
      params.date_from = `${startYear}-${String(startMonth).padStart(2, "0")}-01`;
      params.date_to = `${endYear}-${String(endMonth).padStart(2, "0")}-${String(lastDayOfMonth(endYear, endMonth)).padStart(2, "0")}`;
    }
    const r = await api.get("/tasks", { params });
    setTasks(r.data);
  };
  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/task-categories").then(r => setCats(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientFilter, rangeActive, startYear, startMonth, endYear, endMonth, business]);

  const clientName = id => clients.find(c => c.id === id)?.name || "—";
  const catName = id => cats.find(c => c.id === id)?.name || "—";

  // Column-driven sort. Default = Client asc (then Task asc as a stable
  // secondary key inside a client group).
  const sortedTasks = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: "base" });
    const dir = sortDir === "asc" ? 1 : -1;
    const primary = (a, b) => {
      switch (sortField) {
        case "date":
          return (a.date || "").localeCompare(b.date || "") * dir;
        case "client":
          return collator.compare(clientName(a.client_id), clientName(b.client_id)) * dir;
        case "task":
          return collator.compare(catName(a.task_category_id), catName(b.task_category_id)) * dir;
        case "price":
          return (Number(a.price || 0) - Number(b.price || 0)) * dir;
        default:
          return 0;
      }
    };
    return [...tasks].sort((a, b) => {
      const p = primary(a, b);
      if (p !== 0) return p;
      // Stable secondaries: keep same-client tasks by task name, and same-task rows by date
      if (sortField !== "client") {
        const c = collator.compare(clientName(a.client_id), clientName(b.client_id));
        if (c !== 0) return c;
      }
      if (sortField !== "task") {
        const t = collator.compare(catName(a.task_category_id), catName(b.task_category_id));
        if (t !== 0) return t;
      }
      return (a.date || "").localeCompare(b.date || "");
    });
  }, [tasks, clients, cats, sortField, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const clickSort = (field) => {
    if (field === sortField) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };
  const sortIcon = (field) => {
    if (field !== sortField) return <span className="opacity-30 ml-0.5">↕</span>;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 inline ml-0.5" />
      : <ArrowDown className="w-3 h-3 inline ml-0.5" />;
  };

  const total = useMemo(() => tasks.reduce((s, t) => s + Number(t.price || 0), 0), [tasks]);
  const clientOptions = useMemo(
    () => clients.filter(c => !business || c.business === business),
    [clients, business]
  );

  const saveRef = async (t) => {
    const ref = editingRef[t.id];
    if (ref === undefined) return;
    try {
      await api.patch(`/tasks/${t.id}`, { invoice_ref: ref });
      toast.success("Invoice reference saved");
      setEditingRef(s => { const cp = { ...s }; delete cp[t.id]; return cp; });
      load();
    } catch { toast.error("Could not save"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Outstanding</div>
          <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>Outstanding invoices</h1>
          <p className="text-[#595959] mt-2">All tasks without an invoice reference. Add the reference inline to mark as invoiced.</p>
        </div>
        <div className="np-card !p-3"><BusinessFilter /></div>
      </div>

      <div className="np-card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#0F4F51]" />
          <span className="np-label">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <label className="md:col-span-2 text-xs font-bold text-[#595959]">
            Client
            <select data-testid="filter-client" value={clientFilter} onChange={e => setClientFilter(e.target.value)} className="block w-full mt-1 px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-normal text-[#011617]">
              <option value="">All clients{business ? ` (${business})` : ""}</option>
              {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {/* Start of date range */}
          <div className="md:col-span-3 grid grid-cols-2 gap-2">
            <label className="text-xs font-bold text-[#595959]">
              From
              <div className="flex gap-1 mt-1">
                <select
                  data-testid="filter-start-month"
                  value={startMonth}
                  onChange={e => { setStartMonth(Number(e.target.value)); setRangeActive(true); }}
                  className="flex-1 px-2 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-normal text-[#011617]"
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m.slice(0, 3)}</option>)}
                </select>
                <input
                  data-testid="filter-start-year"
                  type="number"
                  value={startYear}
                  onChange={e => { setStartYear(Number(e.target.value)); setRangeActive(true); }}
                  className="w-20 px-2 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-normal text-[#011617]"
                />
              </div>
            </label>
            <label className="text-xs font-bold text-[#595959]">
              To
              <div className="flex gap-1 mt-1">
                <select
                  data-testid="filter-end-month"
                  value={endMonth}
                  onChange={e => { setEndMonth(Number(e.target.value)); setRangeActive(true); }}
                  className="flex-1 px-2 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-normal text-[#011617]"
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m.slice(0, 3)}</option>)}
                </select>
                <input
                  data-testid="filter-end-year"
                  type="number"
                  value={endYear}
                  onChange={e => { setEndYear(Number(e.target.value)); setRangeActive(true); }}
                  className="w-20 px-2 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-normal text-[#011617]"
                />
              </div>
            </label>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-bold text-transparent select-none">.</span>
            <button
              data-testid="filter-clear"
              onClick={() => { setClientFilter(""); setRangeActive(false); setStartYear(y); setStartMonth(m); setEndYear(y); setEndMonth(m); }}
              className="rounded-lg border border-[#CCCCCC] text-[#011617] font-semibold px-3 py-2.5 hover:border-[#0F4F51]"
            >
              {rangeActive ? "All dates" : "Reset"}
            </button>
          </div>
        </div>
        {rangeActive && (
          <p className="text-[11px] text-[#595959] mt-2">Showing outstanding tasks dated {MONTHS[startMonth - 1]} {startYear} → {MONTHS[endMonth - 1]} {endYear}.</p>
        )}
      </div>

      <div className="np-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            {tasks.length} outstanding {tasks.length === 1 ? "task" : "tasks"}
          </h2>
          <div data-testid="outstanding-total" className="text-lg font-extrabold text-[#0F4F51]" style={{ fontFamily: "Manrope" }}>
            Total: {formatZAR(total)}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="np-table-header">
              <tr>
                <th
                  data-testid="sort-date"
                  onClick={() => clickSort("date")}
                  className="text-left px-3 py-2.5 cursor-pointer hover:text-[#00ACB3] select-none"
                >Date {sortIcon("date")}</th>
                <th
                  data-testid="sort-client"
                  onClick={() => clickSort("client")}
                  className="text-left px-3 py-2.5 cursor-pointer hover:text-[#00ACB3] select-none"
                >Client {sortIcon("client")}</th>
                <th
                  data-testid="sort-task"
                  onClick={() => clickSort("task")}
                  className="text-left px-3 py-2.5 cursor-pointer hover:text-[#00ACB3] select-none"
                >Task {sortIcon("task")}</th>
                <th className="text-left px-3 py-2.5">Notes</th>
                <th className="text-left px-3 py-2.5">Repeat</th>
                <th
                  data-testid="sort-price"
                  onClick={() => clickSort("price")}
                  className="text-right px-3 py-2.5 cursor-pointer hover:text-[#00ACB3] select-none"
                >Price {sortIcon("price")}</th>
                <th className="text-left px-3 py-2.5">Invoice ref</th>
                <th className="text-left px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={8} className="text-center text-[#595959] py-8">Nothing outstanding here. </td></tr>
              )}
              {sortedTasks.map(t => (
                <tr key={t.id} className="border-b border-[#CCCCCC] hover:bg-[#E5FFFF]/40">
                  <td className="px-3 py-2.5 text-[#011617]">{formatDate(t.date)}</td>
                  <td className="px-3 py-2.5 font-semibold text-[#011617]">{clientName(t.client_id)}</td>
                  <td className="px-3 py-2.5 text-[#011617]">{catName(t.task_category_id)}</td>
                  <td className="px-3 py-2.5 text-[#595959] max-w-[160px] truncate">{t.notes}</td>
                  <td className="px-3 py-2.5"><RepeatBadge repeat={t.repeat_type} /></td>
                  <td className="px-3 py-2.5 text-right font-bold text-[#011617]">{formatZAR(t.price)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <input
                        data-testid={`inv-ref-${t.id}`}
                        value={editingRef[t.id] ?? ""}
                        onChange={e => setEditingRef(s => ({ ...s, [t.id]: e.target.value }))}
                        placeholder="e.g. A003"
                        className="w-24 px-2 py-1 rounded border border-[#CCCCCC] outline-none focus:border-[#00ACB3]"
                      />
                      <button
                        data-testid={`save-ref-${t.id}`}
                        onClick={() => saveRef(t)}
                        className="text-[#00ACB3] hover:text-[#0F4F51]"
                        title="Save"
                      ><Save className="w-4 h-4" /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><InvoiceStatusBadge invoiceRef={t.invoice_ref} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
